import type { InfiniteData, QueryClient } from '@tanstack/react-query';
import type {
  ExpenditureCategory,
  ExpenditureDto,
  RowVersion,
  SortInboxResponse,
  TriageItem,
} from '@signalvote/shared';
import { BankTransactionAttribution } from '@signalvote/shared';
import { queryKeys } from '../../lib/queryKeys';

/** A `[queryKey, data]` snapshot of every sort-inbox query, for onError rollback. */
export type SortInboxSnapshot = ReturnType<QueryClient['getQueriesData']>;

/**
 * Optimistically re-stamp the matched sort-inbox row(s) across every cached
 * display-ledger query so a reversible status-flip lands on click — not after the
 * action's POST AND a full sort-inbox refetch complete (two sequential round-trips,
 * the multi-second lag that made treasurers click the same row over and over).
 *
 * `matches` selects the affected row(s); `patch` transforms the matched
 * {@link TriageItem} into its resolved shape — the state the sort-inbox refetch
 * would produce after the action succeeds (see the `mark*` patches below, each
 * mirroring `LedgerStatusProjection.DeriveStatus`). Returns a snapshot the caller
 * restores in `onError`, so a server rejection (e.g. a 409 concurrency conflict)
 * rolls the row back rather than leaving a failed action falsely showing as done.
 *
 * Scoped to the INFINITE display-ledger shape (`{ pages: [{ items }] }`) only.
 * The flat NeedsAction queue (`{ items }`) and the scalar count queries also live
 * under the `['finance','sort-inbox']` prefix; patching every prefix match blind
 * to shape is the `setQueriesData` prefix trap this repo has been bitten by. Those
 * surfaces reconcile on the caller's `onSettled` / `onSuccess` invalidation instead.
 */
export async function optimisticallyPatchSortInboxRow(
  queryClient: QueryClient,
  matches: (item: TriageItem) => boolean,
  patch: (item: TriageItem) => TriageItem,
): Promise<SortInboxSnapshot> {
  // Stop any in-flight sort-inbox refetch from clobbering the optimistic write.
  await queryClient.cancelQueries({ queryKey: queryKeys.finance.sortInboxAll() });
  const snapshot = queryClient.getQueriesData({
    queryKey: queryKeys.finance.sortInboxAll(),
  });
  queryClient.setQueriesData(
    { queryKey: queryKeys.finance.sortInboxAll() },
    (old: unknown) => patchInfiniteLedger(old, matches, patch),
  );
  return snapshot;
}

/** Restore the pre-optimistic sort-inbox cache after a rejected action. */
export function rollbackSortInbox(
  queryClient: QueryClient,
  snapshot: SortInboxSnapshot | undefined,
): void {
  snapshot?.forEach(([key, data]) => queryClient.setQueryData(key, data));
}

function patchInfiniteLedger(
  old: unknown,
  matches: (item: TriageItem) => boolean,
  patch: (item: TriageItem) => TriageItem,
): unknown {
  if (
    !old ||
    typeof old !== 'object' ||
    !('pages' in old) ||
    !Array.isArray((old as InfiniteData<SortInboxResponse>).pages)
  ) {
    return old;
  }
  const data = old as InfiniteData<SortInboxResponse>;
  return {
    ...data,
    pages: data.pages.map((page) => ({
      ...page,
      items: page.items.map((item) => (matches(item) ? patch(item) : item)),
    })),
  };
}

// ────────────────────────────────────────────────────────────────────────────
// Patches — each mirrors the resolved projection the sort-inbox refetch produces
// (`LedgerStatusProjection.DeriveStatus`), so the invalidate-on-settle refetch
// is a visual no-op (no flicker). They set only the row's own status stamp
// (`status` / `actionNeeded` / `resolvedAs`, plus the entity's stamped
// `ledgerStatus` / bank `attribution` for internal coherence) — never the
// server-computed verdict (limit warnings, totals, "needs info", closeability),
// which stays server-truth-driven.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Confirm-due → Done (Pending/born-Pending → Confirmed). `status: 'Done'` routes
 * the Action column to the filled green check, `resolvedAs: 'Confirmed'` labels it,
 * and `actionNeeded: null` clears the NeedsAction dispatch. An Expenditure row's
 * slim DTO flips `ledgerStatus` Pending → Confirmed so the snapshot stays coherent
 * for any immediately-following interaction.
 */
export function markConfirmed(item: TriageItem): TriageItem {
  if (item.kind === 'Expenditure') {
    return {
      ...item,
      status: 'Done',
      actionNeeded: null,
      resolvedAs: 'Confirmed',
      expenditure: { ...item.expenditure, ledgerStatus: 'Confirmed' },
    };
  }
  return { ...item, status: 'Done', actionNeeded: null, resolvedAs: 'Confirmed' };
}

/**
 * Done → NeedsAction/Confirm (Confirmed/Voided → Pending). The inverse of
 * {@link markConfirmed}; serves BOTH unconfirm (uncheck a Done money row) and
 * unvoid (restore a Voided one) — each returns the row to the Pending review
 * queue, where the projection reads NeedsAction/Confirm for a complete row.
 *
 * For UNCONFIRM this is always exact: a Done row is complete by construction
 * (`DeriveStatus` only reaches Done when `IsComplete`), so a Pending complete row
 * reads NeedsAction/Confirm. For UNVOID the client can't see completeness on the
 * slim DTO, so a restored row that's genuinely missing donor-info / a payee
 * address would settle to its `ProvideDonorInfo` / `ProvideExpenditureInfo` flag
 * instead of Confirm — that rarer case is corrected by the invalidate-on-settle
 * refetch (the common complete-row case is exact).
 */
export function markPendingReview(item: TriageItem): TriageItem {
  if (item.kind === 'Expenditure') {
    return {
      ...item,
      status: 'NeedsAction',
      actionNeeded: 'Confirm',
      resolvedAs: null,
      expenditure: { ...item.expenditure, ledgerStatus: 'Pending' },
    };
  }
  if (item.kind === 'Contribution') {
    return {
      ...item,
      status: 'NeedsAction',
      actionNeeded: 'Confirm',
      resolvedAs: null,
      contribution: { ...item.contribution, ledgerStatus: 'Pending' },
    };
  }
  return item;
}

/**
 * A money row (Contribution / Expenditure) excluded from the report → terminal
 * "Not reported" (Done / `resolvedAs: 'Voided'`, ADR-016). The dimmed ⊘ replaces
 * the green check; the stamped `ledgerStatus` flips to Voided.
 */
export function markVoided(item: TriageItem): TriageItem {
  if (item.kind === 'Expenditure') {
    return {
      ...item,
      status: 'Done',
      actionNeeded: null,
      resolvedAs: 'Voided',
      expenditure: { ...item.expenditure, ledgerStatus: 'Voided' },
    };
  }
  if (item.kind === 'Contribution') {
    return {
      ...item,
      status: 'Done',
      actionNeeded: null,
      resolvedAs: 'Voided',
      contribution: { ...item.contribution, ledgerStatus: 'Voided' },
    };
  }
  return item;
}

/**
 * Mark-as-reported (Confirmed → Reported). ONLY the stamped `ledgerStatus` flips:
 * in an OPEN period a Reported-status row still RENDERS Done (ADR-019 D6), so the
 * projection keeps `status: 'Done'` and the kind's resolution word — the refetch
 * changes nothing but `ledgerStatus`. Preserving the render triple (spreading it
 * unchanged) is what mirrors the projection and avoids a flicker.
 */
export function markReported(item: TriageItem): TriageItem {
  if (item.kind === 'Expenditure') {
    return { ...item, expenditure: { ...item.expenditure, ledgerStatus: 'Reported' } };
  }
  if (item.kind === 'Contribution') {
    return { ...item, contribution: { ...item.contribution, ledgerStatus: 'Reported' } };
  }
  return item;
}

/** Reverse of {@link markReported} — unmark (Reported → Confirmed). */
export function markUnreported(item: TriageItem): TriageItem {
  if (item.kind === 'Expenditure') {
    return { ...item, expenditure: { ...item.expenditure, ledgerStatus: 'Confirmed' } };
  }
  if (item.kind === 'Contribution') {
    return { ...item, contribution: { ...item.contribution, ledgerStatus: 'Confirmed' } };
  }
  return item;
}

/**
 * Bank line excluded from the report (Unattributed → ExcludedFromReport, ADR-014):
 * → Done / `resolvedAs: 'Excluded'` (treasurer copy "Not reported"). The slim
 * bank DTO's `attribution` flips to keep the snapshot coherent.
 */
export function markBankExcluded(item: TriageItem): TriageItem {
  if (item.kind !== 'BankLine') return item;
  return {
    ...item,
    status: 'Done',
    actionNeeded: null,
    resolvedAs: 'Excluded',
    bt: { ...item.bt, attribution: BankTransactionAttribution.ExcludedFromReport },
  };
}

/**
 * Bank line reopened (ExcludedFromReport / DepositOfRecordedChecks → Unattributed):
 * → NeedsAction/Classify, back in the un-triaged queue. The inverse of
 * {@link markBankExcluded}.
 */
export function markBankReopened(item: TriageItem): TriageItem {
  if (item.kind !== 'BankLine') return item;
  return {
    ...item,
    status: 'NeedsAction',
    actionNeeded: 'Classify',
    resolvedAs: null,
    bt: { ...item.bt, attribution: BankTransactionAttribution.Unattributed },
  };
}

/** Orphan refund linked to its parent contribution → Done / `resolvedAs: 'Linked'`. */
export function markRefundLinked(item: TriageItem): TriageItem {
  if (item.kind !== 'Refund') return item;
  return { ...item, status: 'Done', actionNeeded: null, resolvedAs: 'Linked' };
}

/** Orphan refund set aside as standalone → Done / `resolvedAs: 'Standalone'`. */
export function markRefundStandalone(item: TriageItem): TriageItem {
  if (item.kind !== 'Refund') return item;
  return { ...item, status: 'Done', actionNeeded: null, resolvedAs: 'Standalone' };
}

/**
 * Refund returned to orphan (unlink a Linked refund / revoke a Standalone one):
 * → NeedsAction/MatchRefund. Both uncheck paths land on the same resolved shape.
 */
export function markRefundOrphan(item: TriageItem): TriageItem {
  if (item.kind !== 'Refund') return item;
  return { ...item, status: 'NeedsAction', actionNeeded: 'MatchRefund', resolvedAs: null };
}

// ────────────────────────────────────────────────────────────────────────────
// Field patch (NOT a status mark) — a data-only re-write of a single displayed
// field. Unlike the `mark*` patches above, this leaves the render triple
// (`status` / `actionNeeded` / `resolvedAs`) ALONE: any derived consequence (e.g.
// a `CategorizeExpenditure` prompt clearing) stays server-truth-driven and lands
// on the caller's invalidate-on-settle refetch.
// ────────────────────────────────────────────────────────────────────────────

/**
 * Re-category an Expenditure row — the inline Category re-pick on the close ledger
 * (root cause #3). The category `<select>` is controlled straight off this cache
 * with no draft state, so without an optimistic write a successful save keeps
 * showing the OLD category (visibly snapping back) until the full sort-inbox
 * refetch commits — a display-correctness problem on a compliance screen. This
 * factory sets ONLY the row's own `expenditure.category`, keeping the server the
 * source of truth for every derived value. A no-op for non-Expenditure rows.
 */
export function patchExpenditureCategory(
  category: ExpenditureCategory,
): (item: TriageItem) => TriageItem {
  return (item) =>
    item.kind === 'Expenditure'
      ? { ...item, expenditure: { ...item.expenditure, category } }
      : item;
}

/**
 * Write a saved Expenditure's dates into its row. The date cells read straight off
 * this cache, so without it a save snaps back until the refetch lands.
 */
export function patchExpenditureSavedDates(
  saved: Pick<ExpenditureDto, 'date' | 'authorizedDate' | 'incurredDate'>,
): (item: TriageItem) => TriageItem {
  return (item) =>
    item.kind === 'Expenditure'
      ? {
          ...item,
          date: saved.date,
          expenditure: {
            ...item.expenditure,
            date: saved.date,
            authorizedDate: saved.authorizedDate,
            incurredDate: saved.incurredDate,
          },
        }
      : item;
}

// ────────────────────────────────────────────────────────────────────────────
// Version reconcile (onSuccess) — write the server's freshly-bumped optimistic-
// concurrency token straight from a mutation's response.
// ────────────────────────────────────────────────────────────────────────────

// ────────────────────────────────────────────────────────────────────────────
// Per-row mutation scope (Layer 1 self-race fix) — serialize every reversible
// action on ONE ledger row so a treasurer racing their own row (check → immediate
// uncheck, re-pick a category twice) can't fire an overlapping request carrying a
// STALE concurrencyVersion. Optimistic-concurrency 409s are meant for CROSS-actor
// conflicts (two tabs/users), never one user's own in-flight request; serializing
// the row closes that self-race at the source, complementing the onSuccess version
// writeback above (the next queued action reads the reconciled version).
// ────────────────────────────────────────────────────────────────────────────

/**
 * Options for a REVERSIBLE ledger mutation hook. `scopeId` is the row-stable
 * {@link TriageItem} id the close ledger threads in so that every action on the SAME
 * row serializes (Layer 1 self-race fix — see {@link rowMutationScope}). Omitted by
 * non-ledger callers, who keep TanStack's default parallel behavior. An options
 * object (not a bare param) leaves room to grow without re-shuffling call sites.
 */
export interface LedgerMutationOptions {
  scopeId?: string;
}

/**
 * Build the TanStack v5 mutation `scope` option that serializes a reversible ledger
 * action against every OTHER action on the same row. Keyed on the row-stable
 * {@link TriageItem} id (`item.id`) — which survives the optimistic control-swap
 * (ConfirmCheckbox → DoneActionCell etc.), so a confirm and an immediate uncheck,
 * though different hooks/observers, share ONE scope id and queue. Different row ids
 * get different scope ids and stay parallel, so bulk "mark many rows done fast"
 * throughput is untouched. Returns `undefined` when no `scopeId` is supplied, so
 * non-ledger callers keep TanStack's default (a unique id per mutation → parallel).
 *
 * The `sort-inbox-row:` prefix namespaces the scope so it can never collide with an
 * unrelated mutation scope that happened to pick the same raw id.
 */
export function rowMutationScope(
  scopeId: string | undefined,
): { id: string } | undefined {
  return scopeId == null ? undefined : { id: `sort-inbox-row:${scopeId}` };
}

/** Set ONLY the row's own `concurrencyVersion` on whichever slim DTO its kind carries. */
function setRowVersion(item: TriageItem, version: RowVersion): TriageItem {
  switch (item.kind) {
    case 'Expenditure':
      return { ...item, expenditure: { ...item.expenditure, concurrencyVersion: version } };
    case 'Contribution':
      return { ...item, contribution: { ...item.contribution, concurrencyVersion: version } };
    case 'Refund':
      return { ...item, refund: { ...item.refund, concurrencyVersion: version } };
    // A bank line's token is a plain number: reconcileSortInboxBankLineVersion writes it.
    case 'BankLine':
    default:
      return item;
  }
}

/**
 * Write the matched row's freshly-bumped `concurrencyVersion` into every cached
 * sort-inbox query, straight from a mutation's server response — run in
 * `onSuccess`. Unlike {@link optimisticallyPatchSortInboxRow} this neither cancels
 * in-flight queries nor snapshots for rollback: it reconciles the row's optimistic
 * token the instant the write commits so a rapid FOLLOW-UP action on the SAME row
 * sends the fresh version, not the stale one.
 *
 * Why the optimistic patch + invalidate-on-settle refetch is not enough: the very
 * next action's `onMutate` calls `cancelQueries`, which kills the invalidate-on-
 * settle refetch that would otherwise have delivered this version. So during rapid
 * interaction the refetch never lands and the cache keeps the STALE version — the
 * 409 that surfaces as "That didn't save" (quick-actions) / "updated elsewhere"
 * (the category editor). A no-op when `version` is null/undefined (e.g. a 204 or a
 * response that omits the token). Scoped, like the patch helpers, to the infinite
 * display-ledger shape only.
 */
export function reconcileSortInboxRowVersion(
  queryClient: QueryClient,
  matches: (item: TriageItem) => boolean,
  version: RowVersion | null | undefined,
): void {
  if (version == null) return;
  reconcileSortInboxRow(queryClient, matches, (item) => setRowVersion(item, version));
}

/**
 * {@link reconcileSortInboxRowVersion} for a bank line, whose token is still a plain number
 * because BankTransaction has not moved to RowVersion.
 */
export function reconcileSortInboxBankLineVersion(
  queryClient: QueryClient,
  matches: (item: TriageItem) => boolean,
  version: number | null | undefined,
): void {
  if (version == null) return;
  reconcileSortInboxRow(queryClient, matches, (item) =>
    item.kind === 'BankLine' ? { ...item, bt: { ...item.bt, concurrencyVersion: version } } : item,
  );
}

/**
 * Apply `patch` to the matched row in every cached display ledger from a
 * mutation's server response, without cancelling in-flight queries or snapshotting.
 */
export function reconcileSortInboxRow(
  queryClient: QueryClient,
  matches: (item: TriageItem) => boolean,
  patch: (item: TriageItem) => TriageItem,
): void {
  queryClient.setQueriesData(
    { queryKey: queryKeys.finance.sortInboxAll() },
    (old: unknown) => patchInfiniteLedger(old, matches, patch),
  );
}
